import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Folder, ArrowRight, Search } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const ClientProjects = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      setFilteredProjects(
        projects.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    } else {
      setFilteredProjects(projects);
    }
  }, [searchQuery, projects]);

  const fetchProjects = async () => {
    try {
      
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
        setFilteredProjects(data.projects);
      }
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-moonstone"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-projects">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-2">My Projects</h2>
        <p className="text-text-secondary">View and manage your campaign projects</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" size={20} />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 glass"
          data-testid="client-projects-search"
        />
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="client-projects-grid">
        {filteredProjects.map((project) => (
          <Link
            key={project.id}
            to={`/project/${project.id}`}
            data-testid={`client-project-card-${project.id}`}
          >
            <Card className="glass metric-card h-full hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-moonstone to-billiard flex items-center justify-center">
                    <Folder className="text-white" size={24} />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    project.status === 'Active' ? 'bg-positive/20 text-positive' : 'bg-neutral/20 text-text-primary'
                  }`}>
                    {project.status}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-text-primary mb-2">{project.name}</h3>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-subtle">
                  <span className="text-sm text-text-secondary">View Performance</span>
                  <ArrowRight className="text-brand" size={20} />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <Folder className="mx-auto text-text-tertiary mb-4" size={48} />
          <h3 className="text-xl font-semibold text-text-primary mb-2">No projects found</h3>
          <p className="text-text-secondary">Your projects will appear here once they're created</p>
        </div>
      )}
    </div>
  );
};

export default ClientProjects;
